// src/components/dashboard/DashboardAnalytics.tsx
//
// MIGRATED — local FDW_BASE / dashboardToken / getDashboardToken /
// authedFetch block removed in favor of the shared dashboardApi client.
// All five call sites updated: '/dashboard/analytics/*' → '/analytics/*'
// (data routes, now under /api).
import React, { useState, useEffect } from 'react';
import { authedFetch } from '../../services/dashboardApi';
import { DiscoveryFunnelChart } from './DiscoveryFunnelChart';
import { ActivityTimelineChart } from './ActivityTimelineChart';
import { FreshnessCard } from './FreshnessCard';
import { CapabilityGapsCard } from './CapabilityGapsCard';
import { RecentEventsTable } from './RecentEventsTable';

interface DiscoveryFunnelData {
  windowDays: number;
  totalEvents: number;
  byFunction: Record<string, number>;
  byOperator: Record<string, number>;
}

interface ActivityTimelineData {
  windowDays: number;
  series: { date: string; count: number }[];
}

interface FreshnessData {
  published: boolean;
  lastUpdatedAt: string | null;
  daysSinceUpdate: number | null;
}

interface CapabilityGapsData {
  gates: Record<string, boolean>;
  gaps: string[];
}

interface RecentEventsData {
  windowDays: number;
  events: {
    id: string; botName: string; operator: string; function: string;
    action: string; path: string | null; method: string; timestamp: string;
  }[];
}

export const DashboardAnalytics: React.FC = () => {
  const [discoveryFunnel, setDiscoveryFunnel] = useState<DiscoveryFunnelData | null>(null);
  const [activityTimeline, setActivityTimeline] = useState<ActivityTimelineData | null>(null);
  const [freshness, setFreshness] = useState<FreshnessData | null>(null);
  const [capabilityGaps, setCapabilityGaps] = useState<CapabilityGapsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentEvents, setRecentEvents] = useState<RecentEventsData | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [funnelRes, timelineRes, freshnessRes, gapsRes, eventsRes] = await Promise.all([
          authedFetch('/analytics/discovery-funnel'),
          authedFetch('/analytics/activity-timeline'),
          authedFetch('/analytics/freshness'),
          authedFetch('/analytics/capability-gaps'),
          authedFetch('/analytics/recent-events'),
        ]);

        if (!funnelRes.ok || !timelineRes.ok || !freshnessRes.ok || !gapsRes.ok || !eventsRes.ok) {
          throw new Error('Failed to load analytics.');
        }

        const [funnelData, timelineData, freshnessData, gapsData, eventsData] = await Promise.all([
          funnelRes.json(), timelineRes.json(), freshnessRes.json(), gapsRes.json(), eventsRes.json(),
        ]);

        if (!cancelled) {
          setDiscoveryFunnel(funnelData);
          setActivityTimeline(timelineData);
          setFreshness(freshnessData);
          setCapabilityGaps(gapsData);
          setRecentEvents(eventsData);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load analytics.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading analytics…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-brand-black">Analytics & Insights</h1>
        <p className="text-sm text-brand-gray mt-1">How agents are discovering and working with you.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-brand-border p-6">
          <h2 className="text-sm font-medium text-brand-black mb-4">Discovery funnel</h2>
          <DiscoveryFunnelChart byFunction={discoveryFunnel?.byFunction ?? {}} />
        </div>
        <div className="rounded-xl border border-brand-border p-6">
          <h2 className="text-sm font-medium text-brand-black mb-4">Activity over time</h2>
          <ActivityTimelineChart series={activityTimeline?.series ?? []} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FreshnessCard
          published={freshness?.published ?? false}
          daysSinceUpdate={freshness?.daysSinceUpdate ?? null}
        />
        <CapabilityGapsCard gaps={capabilityGaps?.gaps ?? []} />
      </div>
      <RecentEventsTable events={recentEvents?.events ?? []} />
    </div>
  );
};